import { Helmet } from 'react-helmet-async'

const SITE_NAME     = 'ABBUSINESS'
const BASE_URL      = 'https://abbusinessgn.com'
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`

/**
 * SEO — Balises meta + Open Graph + Twitter Card + JSON-LD optionnel.
 *
 * Props :
 *   title, description, image, url, type, noindex — standard
 *   jsonLd — objet ou tableau d'objets Schema.org injectés en <script type="application/ld+json">
 */
export default function SEO({
  title,
  description = 'ABBUSINESS — Votre boutique en ligne premium en Guinée. Produits de qualité, livraison rapide, paiement à la livraison.',
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  noindex = false,
  jsonLd = null,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Boutique en ligne premium`
  const canonical = url ? `${BASE_URL}${url}` : BASE_URL

  const schemas = jsonLd
    ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd])
    : []

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type"        content={type} />
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image"       content={image} />
      {/*
        Les dimensions ne sont posees que pour l'image de marque, la seule
        dont on les connaisse. WhatsApp et LinkedIn n'affichent la grande
        vignette que si elles sont declarees ; annoncer 1200x630 pour une
        photo produit de taille inconnue ferait afficher une image deformee,
        ce qui est pire que la miniature.
      */}
      {image === DEFAULT_IMAGE && (
        <meta property="og:image:width" content="1200" />
      )}
      {image === DEFAULT_IMAGE && (
        <meta property="og:image:height" content="630" />
      )}
      <meta property="og:url"         content={canonical} />
      <meta property="og:site_name"   content={SITE_NAME} />
      <meta property="og:locale"      content="fr_GN" />

      {/* Twitter */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={image} />

      {/* Schema.org JSON-LD */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  )
}

/* â”€â”€ Helpers Schema.org â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

/**
 * Génère un schema Product complet pour une page produit.
 *
 * Usage dans ProductDetail.jsx :
 *   <SEO
 *     title={product.name_fr}
 *     description={product.description_fr}
 *     image={product.primary_image}
 *     url={`/produits/${product.slug}`}
 *     type="product"
 *     jsonLd={[
 *       buildProductSchema(product, 'fr'),
 *       buildBreadcrumbSchema([
 *         { name: 'Accueil', url: '/' },
 *         { name: product.category?.name_fr, url: `/catalogue?category=${product.category?.slug}` },
 *         { name: product.name_fr, url: `/produits/${product.slug}` },
 *       ]),
 *     ]}
 *   />
 */
/**
 * Données structurées produit.
 *
 * Cette fonction était écrite, correcte dans son intention, et appelée nulle
 * part : aucune fiche produit n'avait de balisage Schema.org, donc aucun
 * extrait enrichi (prix, disponibilité, note) dans les résultats Google.
 *
 * Elle lisait par ailleurs `name_fr`, `description_fr` et `rating_avg`, trois
 * champs que l'API ne renvoie pas : ProductResource sérialise `name` et
 * `description` comme objets `{fr, en}`, et la note s'appelle `rating`.
 */
export function buildProductSchema(product, lang = 'fr') {
  if (!product) return null

  const pick = (field) =>
    typeof field === 'object' && field !== null
      ? (lang === 'fr' ? (field.fr || field.en) : (field.en || field.fr))
      : field

  const name = pick(product.name) ?? ''
  const desc = pick(product.description) ?? ''

  // Le prix annoncé doit être celui réellement facturé, vente flash comprise.
  const price = product.effective_price ?? product.price
  const image = product.primary_image || product.images?.[0]?.url || DEFAULT_IMAGE

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description: desc.replace(/<[^>]+>/g, '').slice(0, 500),
    image: [image],
    sku: product.slug,
    brand: {
      '@type': 'Brand',
      name: 'ABBUSINESS',
    },
    offers: {
      '@type': 'Offer',
      url: `${BASE_URL}/produits/${product.slug}`,
      priceCurrency: 'GNF',
      price: String(price ?? 0),
      priceValidUntil: product.flash_sale_ends_at
        ? String(product.flash_sale_ends_at).slice(0, 10)
        : new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
      itemCondition: 'https://schema.org/NewCondition',
      availability: product.stock > 0
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Organization',
        name: 'ABBUSINESS',
        url: BASE_URL,
      },
    },
  }

  // Google rejette un aggregateRating sans avis : ne le poser que s'il en
  // existe réellement.
  if (product.rating > 0 && product.rating_count > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: String(Number(product.rating).toFixed(1)),
      reviewCount: String(product.rating_count),
      bestRating: '5',
      worstRating: '1',
    }
  }

  return schema
}

/**
 * Génère un schema BreadcrumbList.
 *
 * @param {Array<{name: string, url: string}>} items
 */
export function buildBreadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${BASE_URL}${item.url}`,
    })),
  }
}

/**
 * Schema Organization pour la page d'accueil.
 */
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'ABBUSINESS',
  url: BASE_URL,
  logo: `${BASE_URL}/logo.png`,
  image: DEFAULT_IMAGE,
  description: 'Boutique en ligne premium en Guinée — mobilier, électroménager, décoration.',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Conakry',
    addressCountry: 'GN',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    availableLanguage: ['French'],
  },
  sameAs: [
    'https://abbusinessgn.com',
  ],
}

